import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import html2canvas from "html2canvas";
import api, { qs } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { brandColor, basisLabel, fmt, pct, signed } from "@/lib/utils-msip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trophy, Download, Award } from "lucide-react";

const TIER = {
  gold: { label: "GOLD", bg: "#FDF3D7", fg: "#8a6d00", accent: "#D4AF37" },
  silver: { label: "SILVER", bg: "#EEF1F5", fg: "#4b5563", accent: "#9CA3AF" },
  bronze: { label: "BRONZE", bg: "#F7E9DC", fg: "#8a4b12", accent: "#B87333" },
};

const TierBadge = ({ tier }) =>
  tier ? (
    <span className="rounded px-1.5 py-0.5 text-[10px] font-extrabold tracking-wider" style={{ background: TIER[tier].bg, color: TIER[tier].fg }}>
      {TIER[tier].label}
    </span>
  ) : null;

export default function League() {
  const { filters } = useApp();
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("");
  const [period, setPeriod] = useState(filters.period === "ytd" ? "ytd" : "mtd");
  const [award, setAward] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    setPeriod(filters.period === "ytd" ? "ytd" : "mtd");
  }, [filters.period]);

  useEffect(() => {
    api
      .get(`/league?${qs({ brand: filters.brand, basis: filters.basis, month, period })}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [filters.brand, filters.basis, month, period]);

  if (!data) return <div className="text-sm text-muted-foreground py-20 text-center">Loading…</div>;

  const downloadCard = async () => {
    const canvas = await html2canvas(cardRef.current, { scale: 2, backgroundColor: null });
    const a = document.createElement("a");
    a.download = `award_${award.rto}_${data.month}.png`;
    a.href = canvas.toDataURL("image/png");
    a.click();
  };

  return (
    <div className="space-y-5" data-testid="league-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight">RTO League & Rewards</h1>
        <span className="text-xs text-muted-foreground">{filters.brand} · {basisLabel(filters.basis)} · Target {pct(data.target)} · min TIV {data.min_tiv}{period === "ytd" && data.ytd_from ? ` · Calendar YTD from ${data.ytd_from}` : ""}</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center rounded-md border overflow-hidden text-xs">
            {[{ v: "mtd", l: "MTD" }, { v: "ytd", l: "YTD" }].map(({ v, l }) => (
              <button
                key={v}
                data-testid={`league-period-${v}`}
                onClick={() => setPeriod(v)}
                className={`px-3 py-1.5 font-medium transition-colors duration-200 ${period === v ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`}
              >
                {l}
              </button>
            ))}
          </div>
          <span className="text-[11px] text-muted-foreground">{period === "ytd" ? "YTD through" : "League month"}</span>
          <Select value={data.month || undefined} onValueChange={setMonth}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid="league-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[...(data.months || [])].reverse().map((m) => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="text-xs text-muted-foreground border bg-card rounded-md p-3 flex flex-wrap gap-x-6 gap-y-1" data-testid="league-weights-info">
        <span><b>Scoring:</b></span>
        <span>{data.weights.achievement} pts · target achievement (capped 125%)</span>
        <span>{data.weights.delta} pts · {period === "ytd" ? "Δ YTD share vs prior month's YTD" : "Δ share MoM"}</span>
        <span>{data.weights.rival_gap} pts · gap-to-{data.rival} improvement</span>
        <span>{data.weights.consistency} pts · consistency (last 6 mo ≥ target)</span>
      </div>

      {data.leagues.length === 0 && <p className="text-sm text-muted-foreground">No eligible RTOs (TIV ≥ 100) for this month.</p>}

      <div className="grid xl:grid-cols-3 gap-4 items-start">
        {data.leagues.map((lg) => (
          <div key={lg.district} className="border bg-card rounded-md overflow-hidden" data-testid={`league-${lg.district.replace(/\s/g, "-")}`}>
            <div className="px-4 py-3 border-b flex items-center gap-2">
              <Trophy size={15} className="text-amber-600" />
              <h3 className="font-heading text-sm font-bold">{lg.district}</h3>
              <span className="text-xs text-muted-foreground ml-auto">{lg.rows.length} eligible</span>
            </div>
            <table className="w-full text-xs">
              <thead className="border-b">
                <tr>
                  {["#", "RTO", "Score", "Share", "Δ MoM", "Tier", ""].map((h, i) => (
                    <th key={i} className={`px-2 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground ${i >= 2 && i <= 4 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lg.rows.map((r) => (
                  <tr key={r.rto} className="border-b last:border-0 hover:bg-muted/50" title={`Achievement ${r.components.achievement} · Δ ${r.components.delta} · Rival gap ${r.components.rival_gap} · Consistency ${r.components.consistency} (${r.consistency_months}/6)`}>
                    <td className="px-2 py-1.5 num text-muted-foreground">{r.rank}</td>
                    <td className="px-2 py-1.5 font-medium whitespace-nowrap">
                      <Link to={`/rto/${encodeURIComponent(r.rto)}`} className="hover:underline">{r.rto}</Link>
                    </td>
                    <td className="px-2 py-1.5 text-right num font-bold">{r.score}</td>
                    <td className="px-2 py-1.5 text-right num">{pct(r.share)}</td>
                    <td className={`px-2 py-1.5 text-right num ${r.delta_share > 0 ? "text-emerald-700" : r.delta_share < 0 ? "text-red-600" : "text-muted-foreground"}`}>{r.delta_share != null ? signed(r.delta_share) : "—"}</td>
                    <td className="px-2 py-1.5"><TierBadge tier={r.tier} /></td>
                    <td className="px-2 py-1.5 text-right">
                      {r.tier && (
                        <button className="text-muted-foreground hover:text-amber-600 transition-colors duration-200" title="Award card" data-testid={`award-btn-${r.rto}`} onClick={() => setAward(r)}>
                          <Award size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <Dialog open={!!award} onOpenChange={(o) => !o && setAward(null)}>
        <DialogContent className="max-w-lg" data-testid="award-dialog">
          <DialogHeader><DialogTitle className="font-heading">Award card</DialogTitle></DialogHeader>
          {award && (
            <>
              <div ref={cardRef} className="rounded-lg p-8 text-white relative overflow-hidden" style={{ background: "#101623" }}>
                <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: TIER[award.tier]?.accent || brandColor(filters.brand) }} />
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/60 font-semibold">Incheon Kia MIS · RTO League · {data.month}{period === "ytd" ? " · YTD" : ""}</div>
                <div className="font-heading text-3xl font-extrabold mt-3">{award.rto}</div>
                <div className="text-sm text-white/70">{award.district}</div>
                <div className="mt-5 flex items-end gap-6">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-white/50">Tier</div>
                    <div className="font-heading text-xl font-extrabold" style={{ color: TIER[award.tier]?.accent }}>{TIER[award.tier]?.label} · #{award.rank}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-white/50">Performance Index</div>
                    <div className="font-heading text-xl font-extrabold">{award.score} / 100</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-white/50">{filters.brand} share</div>
                    <div className="font-heading text-xl font-extrabold" style={{ color: "#fff" }}>{pct(award.share)}</div>
                  </div>
                </div>
                <div className="mt-5 text-[11px] text-white/50">TIV {fmt(award.tiv)} · Δ MoM {award.delta_share != null ? signed(award.delta_share) : "—"} pts · Consistency {award.consistency_months}/6 · {basisLabel(filters.basis)}</div>
              </div>
              <Button onClick={downloadCard} data-testid="award-download-btn"><Download size={14} className="mr-1.5" /> Download PNG</Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
